import {
  AdminDocument,
  AdminGetContentsOfFileRequestParams,
  AdminGetFileRequestParams,
  AdminService,
  DocumentStatusHistory,
  ModelFile,
} from '@admin-api/index';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { SubmitDocumentDialogComponent } from '@shared/components/submit-document-dialog/submit-document-dialog.component';
import { Observable } from 'rxjs';
import { fuseAnimations } from '../../../../@fuse/animations';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from '../../../core/constants';
import { RejectDocumentDialogComponent } from '../reject-document/reject-document-dialog.component';
import { VerifyDocumentDialogComponent } from '../verify-document/verify-document-dialog.component';
import { ViewDocumentDialogComponent } from '../view-document-dialog/view-document-dialog.component';

class DocumentViewModel {
  id: string;
  requirement: string;
  type: string;
  requested_on: string;
  submitted_on: string;
  status: string;
  history: DocumentStatusHistory[];
  fileId?: string;
  fileIds?: string[];
  writtenResponse?: string;
  rejectedReason?: AdminDocument.RejectedReasonEnum;
  requestNotes?: string;
  hasActions: boolean;
}

@Component({
  selector: 'document-list',
  templateUrl: './document-list.component.html',
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None,
})
export class DocumentListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) private _paginator: MatPaginator;
  @Input() accountId: string;
  @Input() refreshData$: Observable<boolean>;
  @Output() refreshTimeline = new EventEmitter<boolean>();

  public dataSource = new MatTableDataSource<DocumentViewModel>();
  public documentsCount: number = 0;
  public pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  public dialogRef: any;

  errorMessage: string = null;
  isLoading: boolean = true;

  public documentsTableColumns: string[] = [
    'requirement',
    'type',
    'requested_on',
    'submitted_on',
    'status',
    'view_download',
    'actions',
  ];
  searchInputControl: UntypedFormControl = new UntypedFormControl();

  private viewDocumentDialogRef: MatDialogRef<ViewDocumentDialogComponent>;
  private rejectDocumentDialogRef: MatDialogRef<RejectDocumentDialogComponent>;
  private verifyDocumentDialogRef: MatDialogRef<VerifyDocumentDialogComponent>;

  constructor(
    private _adminService: AdminService,
    private _matDialog: MatDialog,
    private _snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.dataSource.paginator = this._paginator;
    this.retrieveFilteredDocuments();

    this.refreshData$.subscribe((refresh) => {
      if (refresh) {
        this.retrieveFilteredDocuments();
      }
    });
  }

  ngAfterViewInit(): void {
    this._paginator.page.subscribe(() => {
      this.retrieveFilteredDocuments();
    });
  }

  ngOnDestroy(): void {}

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  retrieveFilteredDocuments() {
    let offset = 0;
    let limit = DEFAULT_PAGE_SIZE;
    if (this._paginator) {
      offset = this._paginator.pageIndex * this._paginator.pageSize;
      limit = this._paginator.pageSize;
    }

    this.retrieveDocuments(this.accountId, offset, limit);
  }

  retrieveDocuments(accountId: string, offset = 0, limit = DEFAULT_PAGE_SIZE) {
    this.isLoading = true;

    this._adminService
      .adminListDocuments({ accountId, offset, limit })
      .subscribe(
        (res) => {
          this.dataSource.data = this.documentDataToViewModel(res.items);
          this.documentsCount = res.total;
        },
        (error) => {
          this.isLoading = false;
          if (error?.error?.message) {
            this.errorMessage = 'Error: ' + error.error.message;
          } else if (error?.message) {
            this.errorMessage = 'Error: ' + error.message;
          } else {
            this.errorMessage = 'Unknown error';
          }
        },
        () => {
          this.isLoading = false;
        },
      );
  }

  private documentDataToViewModel(
    documents: AdminDocument[],
  ): DocumentViewModel[] {
    let viewModel: DocumentViewModel[] = [];

    for (const doc of documents) {
      let requestTime: string;
      let submitTime: string;
      if (doc.status_history?.length > 0) {
        for (const history of doc.status_history) {
          if (history.status === AdminDocument.StatusEnum.REQUESTED) {
            requestTime = history.status_date;
          }
          if (history.status === AdminDocument.StatusEnum.SUBMITTED) {
            submitTime = history.status_date;
          }
        }
      }
      viewModel.push({
        id: doc.id,
        requirement: doc.subtype,
        type: doc.type,
        requested_on: requestTime,
        submitted_on: submitTime,
        status: doc.status.toLocaleLowerCase(),
        history: doc.status_history,
        fileId: doc.file_id,
        fileIds: doc.file_ids,
        writtenResponse: doc.written_response,
        rejectedReason: doc.rejected_reason,
        requestNotes: doc.request_notes,
        hasActions: doc.status === AdminDocument.StatusEnum.SUBMITTED,
      });
    }

    return viewModel;
  }

  verifyDocument(documentId: string) {
    this.verifyDocumentDialogRef = this._matDialog.open(
      VerifyDocumentDialogComponent,
      {
        panelClass: 'verify-document-dialog',
      },
    );

    this.verifyDocumentDialogRef.afterClosed().subscribe((res: boolean) => {
      if (!res) {
        return;
      }
      this._adminService
        .adminUpdateDocument({
          id: documentId,
          accountId: this.accountId,
          adminUpdateDocumentParams: {
            status: AdminDocument.StatusEnum.VERIFIED,
          },
        })
        .subscribe({
          next: (document) => {
            this.retrieveFilteredDocuments();
          },
          error: (error) => {
            this._snackBar.open(error.error?.message, null, {
              duration: 3000,
            });
          },
        });
    });
  }

  rejectDocument(documentId: string) {
    this.rejectDocumentDialogRef = this._matDialog.open(
      RejectDocumentDialogComponent,
      {
        panelClass: 'reject-document-dialog',
      },
    );

    this.rejectDocumentDialogRef
      .afterClosed()
      .subscribe((res: AdminDocument.RejectedReasonEnum) => {
        if (!res) {
          return;
        }
        this._adminService
          .adminUpdateDocument({
            id: documentId,
            accountId: this.accountId,
            adminUpdateDocumentParams: {
              status: AdminDocument.StatusEnum.REJECTED,
              rejected_reason: res,
            },
          })
          .subscribe({
            next: (document) => {
              this.retrieveFilteredDocuments();
            },
            error: (error) => {
              this._snackBar.open(error.error?.message, null, {
                duration: 3000,
              });
            },
          });
      });
  }

  public async uploadFile(
    documentId: string,
    type: AdminDocument.TypeEnum,
    requirement: string,
  ): Promise<void> {
    this.dialogRef = this._matDialog.open(SubmitDocumentDialogComponent, {
      panelClass: 'update-doc-dialog',
      data: {
        documentId: documentId,
        type: type,
        requirement: requirement,
        accountId: this.accountId,
      },
    });

    this.dialogRef.afterClosed().subscribe((success: true) => {
      if (!success) {
        return;
      }
      this.retrieveFilteredDocuments();
      this.refreshTimeline.emit(true);
    });
  }

  public async viewFile(
    fileId?: string,
    writtenResponse?: string,
  ): Promise<void> {
    if (writtenResponse) {
      this.viewDocumentDialogRef = this._matDialog.open(
        ViewDocumentDialogComponent,
        {
          panelClass: 'view-doc-dialog',
          data: {
            file: null,
            data: null,
            textResponse: writtenResponse,
          },
          maxHeight: '100vh',
        },
      );

      this.viewDocumentDialogRef.afterClosed().subscribe((res: any) => {});
      return;
    }

    let fileParams: AdminGetFileRequestParams = {
      accountId: this.accountId,
      id: fileId,
    };

    this._adminService.adminGetFile(fileParams).subscribe({
      next: (file) => {
        if (file) {
          let modelFile = file as ModelFile;
          let contentParams: AdminGetContentsOfFileRequestParams = {
            accountId: this.accountId,
            fileId: file.id,
          };

          this._adminService.adminGetContentsOfFile(contentParams).subscribe({
            next: (res) => {
              this.viewDocumentDialogRef = this._matDialog.open(
                ViewDocumentDialogComponent,
                {
                  panelClass: 'view-doc-dialog',
                  data: {
                    file: file,
                    data: res,
                    filename: modelFile.filename,
                  },
                  height: '100%',
                  maxHeight: '100vh',
                },
              );

              this.viewDocumentDialogRef
                .afterClosed()
                .subscribe((res: any) => {});
            },
            error: (err) => {
              this._snackBar.open(
                'Could not view file: ' + err?.status + ' ' + err?.statusText,
                null,
                {
                  duration: 5000,
                },
              );
            },
          });
        }
      },
      error: (error) => {
        if (error?.error?.message) {
          this.errorMessage = 'Error: ' + error.error.message;
        } else if (error?.message) {
          this.errorMessage = 'Error: ' + error.message;
        } else {
          this.errorMessage = 'Unknown error';
        }
      },
      complete: () => {},
    });
  }

  public async downloadFile(fileId: string): Promise<void> {
    let fileParams: AdminGetFileRequestParams = {
      accountId: this.accountId,
      id: fileId,
    };

    this._adminService.adminGetFile(fileParams).subscribe({
      next: (file) => {
        if (file) {
          let modelFile = file as ModelFile;
          let contentParams: AdminGetContentsOfFileRequestParams = {
            accountId: this.accountId,
            fileId: file.id,
          };

          let mimeType: string;
          switch (file.type) {
            case ModelFile.TypeEnum.PDF:
              mimeType = 'application/pdf';
              break;
            case ModelFile.TypeEnum.JPG:
              mimeType = 'image/jpg';
              break;
            case ModelFile.TypeEnum.PNG:
              mimeType = 'image/png';
              break;
            case ModelFile.TypeEnum.TIFF:
              mimeType = 'image/tiff';
              break;
            default:
              return;
          }

          this._adminService.adminGetContentsOfFile(contentParams).subscribe({
            next: (res) => {
              const blob = new Blob([res], { type: mimeType });
              const objectUrl = window.URL.createObjectURL(blob);
              const a = document.createElement('a') as HTMLAnchorElement;
              a.href = objectUrl;
              a.download = modelFile.filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(objectUrl);
            },
            error: (err) => {
              this._snackBar.open(
                'Could not download file: ' +
                  err?.status +
                  ' ' +
                  err?.statusText,
                null,
                {
                  duration: 5000,
                },
              );
            },
          });
        }
      },
      error: (error) => {
        if (error?.error?.message) {
          this.errorMessage = 'Error: ' + error.error.message;
        } else if (error?.message) {
          this.errorMessage = 'Error: ' + error.message;
        } else {
          this.errorMessage = 'Unknown error';
        }
      },
      complete: () => {},
    });
  }

  openSnackBar(message: string, action: string, warn: boolean): void {
    const snackTheme = warn ? 'mat-warn' : 'mat-primary';
    this._snackBar.open(message, action, {
      duration: 10000,
      verticalPosition: 'top',
      panelClass: ['mat-toolbar', snackTheme],
    });
  }
}
